import React, { useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import styles from '../Dashboards.mod.less';
import Spinner from '../../common/Spinner';
import { useAhaPanelContents, useHtmlSnapshot } from '../hooks';
import ReleaseBurndownChart from 'javascripts/release_burndown_chart';
import AhaContents from './AhaContents';
import ProgressEmpty from './ProgressEmpty';
import { debounce } from 'lodash';
import d3 from 'd3';
import { openPanelModal } from '../store/actions';
import customElementBoolAttr from 'javascripts/util/customElementBoolAttr';

// Note: When there ths chart is rendered with a very large date range (i.e. more than a year),
// we have a lot of elements on the screen which get rebuilt every time a user resizes the panel
// this can have performance issues.
const Progress = ({
  panel,
  preview,
  padding,
  bookmarkId,
  publishing,
  canEdit,
  filters,
}) => {
  const shouldUseHtmlSnapshot = useHtmlSnapshot();
  const ahaContents = useAhaPanelContents({
    panel,
    bookmarkId,
    preview,
    filters,
    showLegend: panel.showLegend,
    dashboardFilter: false,
    panelUnconfigured: !panel.release,
  });

  const panelContentRef = useRef(null);

  const renderChart = useCallback(() => {
    if ((ahaContents || shouldUseHtmlSnapshot) && panelContentRef.current) {
      requestAnimationFrame(() => {
        try {
          const release_id = `release-burndown-chart-${panel.release.get(
            'id'
          )}${preview ? '-preview' : ''}`;
          const releaseElement = document.getElementById(release_id);
          releaseElement.textContent = '';

          const data = JSON.parse(releaseElement.getAttribute('data-json'));
          data.panel = true;
          const chart = ReleaseBurndownChart();

          d3.select('#' + release_id)
            .datum(data)
            .call(chart);
        } catch (error) {
          console.log(error);
        }
      });
    }
  }, [ahaContents, preview, shouldUseHtmlSnapshot, panel]);

  useEffect(renderChart, [renderChart]);

  useEffect(() => {
    const handleWindowResize = debounce(() => {
      renderChart();
    }, 150);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  let panelContent;
  let isLoading = true;
  if (!panel.release) {
    panelContent = (
      <ProgressEmpty
        padding={padding}
        panel={panel}
        publishing={publishing}
        canEdit={canEdit}
      />
    );
  } else if (ahaContents || shouldUseHtmlSnapshot) {
    panelContent = <AhaContents ahaContents={ahaContents} padding={padding} />;
    isLoading = false;
  } else {
    panelContent = <Spinner />;
  }

  return (
    <div
      className={styles.PanelContent}
      aria-label='Progress Panel'
      ref={panelContentRef}
      data-async-loading={customElementBoolAttr(isLoading)}
    >
      {panelContent}
    </div>
  );
};

const mapStateToProps = state => ({
  bookmarkId: state.getIn(['meta', 'bookmarkId']),
  canEdit: state.getIn(['meta', 'canEdit']),
  filters: state.getIn(['meta', 'filters']),
});

const mapDispatchToProps = { openPanelModal };

export default connect(mapStateToProps, mapDispatchToProps)(Progress);
